/**
 * The two builders every validator reports with — a `reference_broken`
 * failure, and the duplicate sweep over one id family — in a leaf of their
 * own (D226): they were born in `semantic-validation.ts`, which grew past
 * the 500-line cap, and the walkers it calls imported them back from it, an
 * import cycle that worked only because nothing ran at module load.
 *
 * @packageDocumentation
 */

import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * Build one `reference_broken` failure.
 *
 * @param referencePath - JSON-path-ish locator of the referring field
 * @param targetId - The id the reference pointed at
 * @param message - Author-facing explanation
 * @returns The failure
 */
export const brokenReference = (referencePath: string, targetId: string, message: string): ReferenceBrokenFailure => ({
	message,
	referencePath,
	targetId,
	type: 'reference_broken',
});

/**
 * Collect duplicate-declaration failures for one id family. Exported for the
 * image checks (R23), which live in their own module but declare ids in
 * exactly this family shape.
 *
 * @param declaredIds - Every declared id in declaration order, paired with its path
 * @param familyName - Author-facing family name (`room`, `item`, `flag`, `exit`)
 * @param failures - Accumulator
 * @returns The set of unique ids in the family (for reference checks)
 */
export const collectDuplicates = (
	declaredIds: readonly { readonly declarationPath: string; readonly declaredId: string; }[],
	familyName: string,
	failures: ReferenceBrokenFailure[],
): ReadonlySet<string> => {
	const seenIds = new Set<string>();

	for (const { declarationPath, declaredId } of declaredIds) {
		if (seenIds.has(declaredId)) {
			failures.push(
				brokenReference(
					declarationPath,
					declaredId,
					`duplicate ${familyName} id — every reference to "${declaredId}" is now ambiguous`,
				),
			);
		}
		seenIds.add(declaredId);
	}

	return seenIds;
};

/**
 * Push one broken-reference failure when an id is not in its declared family.
 *
 * @param declaredIds - The family the reference must resolve in
 * @param referencedId - The id the author wrote
 * @param referencePath - The path of the referring field
 * @param message - Author-facing explanation
 * @param failures - Accumulator
 */
export const requireDeclared = (
	declaredIds: ReadonlySet<string>,
	referencedId: string,
	referencePath: string,
	message: string,
	failures: ReferenceBrokenFailure[],
): void => {
	if (!declaredIds.has(referencedId)) {
		failures.push(brokenReference(referencePath, referencedId, message));
	}
};
